import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { loadNames } from '../api.js'

// Plain text input with an autocomplete dropdown of previously-used names.
// Names are fetched lazily on first focus and cached in localState for the
// lifetime of the component. The dropdown is portaled to document.body and
// positioned via the input's bounding rect, so it escapes any modal overflow
// clipping.
const MAX_VISIBLE = 12

export function NameInput({ value, onChange, placeholder }) {
  const [items, setItems]       = useState(null) // null = not loaded yet
  const [loading, setLoading]   = useState(false)
  const [open, setOpen]         = useState(false)
  const [activeIdx, setActive]  = useState(-1)
  const [rect, setRect]         = useState(null)
  const inputRef = useRef(null)
  const listRef  = useRef(null)

  async function ensureLoaded() {
    if (items !== null || loading) return
    setLoading(true)
    try {
      const res = await loadNames()
      setItems(res?.items ?? [])
    } catch (e) {
      console.error('[NameInput] loadNames failed', e)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  // Filter + rank matches. When query is empty, show the most frequent names.
  const matches = useMemo(() => {
    if (!items) return []
    const q = (value || '').trim().toLowerCase()
    const pool = q ? items.filter(i => i.name.toLowerCase().includes(q)) : items
    const filtered = pool.filter(i => i.name.toLowerCase() !== q)
    const sorted = [...filtered].sort((a, b) => {
      if (q) {
        const aStarts = a.name.toLowerCase().startsWith(q) ? 0 : 1
        const bStarts = b.name.toLowerCase().startsWith(q) ? 0 : 1
        if (aStarts !== bStarts) return aStarts - bStarts
      }
      return b.n - a.n
    })
    return sorted.slice(0, MAX_VISIBLE)
  }, [items, value])

  useEffect(() => { setActive(-1) }, [matches])

  // Track the input's bounding rect so the portaled list can sit right below.
  // Listens to capture-phase scroll so nested-modal scroll also triggers updates.
  useEffect(() => {
    if (!open) return
    function update() {
      if (!inputRef.current) return
      setRect(inputRef.current.getBoundingClientRect())
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open])

  // Close on outside click (excluding both the input wrap AND the portaled list)
  useEffect(() => {
    if (!open) return
    function onDown(e) {
      if (inputRef.current && inputRef.current.contains(e.target)) return
      if (listRef.current  && listRef.current.contains(e.target))  return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
    }
  }, [open])

  function pick(name) {
    onChange(name)
    setOpen(false)
  }

  function onKeyDown(e) {
    if (!open || matches.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive(i => (i + 1) % matches.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(i => (i <= 0 ? matches.length - 1 : i - 1))
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault()
      pick(matches[activeIdx].name)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        className="name-input"
        placeholder={placeholder}
        value={value}
        onChange={e => { onChange(e.target.value); if (!open) setOpen(true) }}
        onFocus={() => { ensureLoaded(); setOpen(true) }}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {open && matches.length > 0 && rect && createPortal(
        <ul
          ref={listRef}
          className="name-input-list"
          role="listbox"
          style={{
            position: 'fixed',
            top:   rect.bottom + 4,
            left:  rect.left,
            width: rect.width,
          }}
        >
          {matches.map((m, i) => (
            <li
              key={m.name}
              role="option"
              aria-selected={i === activeIdx}
              className={`name-input-item${i === activeIdx ? ' is-active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(m.name) }}
              onMouseEnter={() => setActive(i)}
            >
              <span className="name-input-item-name">{m.name}</span>
              <span className="name-input-item-count">{m.n}</span>
            </li>
          ))}
        </ul>,
        document.body
      )}
    </>
  )
}
